import { useState } from "react";
import { ShieldCheck, ShieldAlert, ArrowRight, ArrowLeft, Loader2, LifeBuoy, Sparkles } from "lucide-react";
import { C } from "../../theme/tokens";
import { PrimaryButton } from "../common/primitives";
import { openExternalLink } from "../../utils/video";
import { MaintenanceNotice } from "./MaintenanceScreen";
import { RegLinkButton } from "./RegLinkButton";

// Same support contact used by AccountRestrictedOverlay — kept in sync so
// both "stuck" states (blocked account, can't verify a registration ID)
// point the user to the same place.
const SUPPORT_URL = "https://t.me/EXP_SUPPORT";

const trialFieldStyle = (hasError) => ({
  width: "100%",
  background: C.panel1,
  border: `1px solid ${hasError ? "#C4604A" : C.hairline}`,
  borderRadius: 14,
  color: C.primary,
  fontFamily: "'Space Grotesk', sans-serif",
  fontWeight: 600,
  fontSize: 15,
  padding: "15px 14px",
  outline: "none",
  marginBottom: 12,
});

export function SupportButton({ label }) {
  return (
    <button
      onClick={() => openExternalLink(SUPPORT_URL)}
      style={{
        marginTop: 10,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: "15px 18px",
        borderRadius: 14,
        border: "none",
        background: `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
        color: "#0b0d13",
        fontFamily: "'Space Grotesk', sans-serif",
        fontWeight: 700,
        fontSize: 14.5,
        letterSpacing: "-0.01em",
        cursor: "pointer",
        boxShadow: `0 8px 26px -8px ${C.violetBright}99`,
      }}
    >
      <LifeBuoy size={17} />
      {label}
    </button>
  );
}

/* Shown when the user taps "already have an account" — walks them
   through abandoning the old Pocket Option account and creating a
   fresh one strictly through our link, since that's the only account
   we can see/attribute in our system. */
function SwitchAccountScreen({ t, wide, onBack }) {
  const steps = [t.reg_switch_step1, t.reg_switch_step2, t.reg_switch_step3, t.reg_switch_step4, t.reg_switch_step5];

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.deep,
              border: `1px solid ${C.green}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <ShieldCheck size={24} color={C.greenBright} />
          </div>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {t.reg_switch_title}
          </div>

          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
            {t.reg_switch_desc1}
          </p>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
            {t.reg_switch_desc2}
          </p>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 12 }}>
            {t.reg_switch_desc3}
          </p>

          <div
            style={{
              marginTop: 18,
              borderRadius: 16,
              background: C.panel1,
              border: `1px solid ${C.hairline}`,
              padding: "16px 16px",
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {steps.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                <div
                  style={{
                    flex: "0 0 auto",
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: C.deep,
                    border: `1px solid ${C.green}`,
                    color: C.greenBright,
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontWeight: 700,
                    fontSize: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: 1,
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ color: C.primary, fontSize: 13.5, lineHeight: 1.5 }}>{step}</div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 16,
              color: C.greenBright,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
            }}
          >
            {t.reg_switch_note}
          </div>

          <RegLinkButton t={t} label={t.reg_switch_link} />

          <button
            onClick={onBack}
            style={{
              marginTop: 10,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "15px 18px",
              borderRadius: 14,
              border: `1px solid ${C.hairline}`,
              background: "transparent",
              color: C.primary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 14.5,
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={16} />
            {t.reg_switch_back}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Shown instead of the registration form when the backend reports
   registration is currently switched off (GET .../user/reg-section ->
   { is_active: false }). This only fires for brand-new Telegram users
   (auth returned status: "not_found") — it never affects anyone who
   already has a linked account. No way to dismiss/retry from here
   besides support, matching AccountRestrictedOverlay's pattern for
   the other "stuck" state. */
export function RegistrationClosedScreen({ t, wide, maintenanceMode = false }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
            textAlign: "center",
          }}
        >
          {maintenanceMode && <MaintenanceNotice t={t} />}
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: "#C4604A1f",
              border: "1px solid #C4604A55",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 18px",
            }}
          >
            <ShieldAlert size={26} color="#C4604A" />
          </div>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {t.reg_closed_title}
          </div>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
            {t.reg_closed_desc}
          </p>

          <SupportButton label={t.reg_support} />
        </div>
      </div>
    </div>
  );
}

/* Shown instead of the account-id form when the user taps "start a
   free trial" — no code involved, the backend just grants it and
   meters usage itself (5 signals per timeframe). Posts to
   /api/user/trial (see useAuth.js). For a Telegram session the
   already-verified identity is enough, so there's nothing to fill in
   at all — just a confirm button. For a browser session, email +
   password are still needed to create the account itself. */
export function TrialPeriodScreen({ t, authState, error, onSubmit, onBack, wide, isBrowser = false, busy = false }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const submitting = busy || authState === "linking";
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = (!isBrowser || (emailValid && password.length > 0)) && !submitting;

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (isBrowser) {
      onSubmit(email.trim().toLowerCase(), password);
    } else {
      onSubmit();
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.deep,
              border: `1px solid ${C.green}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <ShieldCheck size={24} color={C.greenBright} />
          </div>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {t.reg_trial_title}
          </div>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
            {t.reg_trial_desc}
          </p>

          <div style={{ marginTop: 18 }}>
            {isBrowser && (
              <>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t.auth_login_placeholder}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  style={trialFieldStyle(email.length > 0 && !emailValid)}
                />
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.auth_password_placeholder}
                  type="password"
                  autoComplete="new-password"
                  style={trialFieldStyle(false)}
                />
                {email.length > 0 && !emailValid && (
                  <div style={{ color: "#C4604A", fontSize: 12.5, marginBottom: 8 }}>
                    {t.auth_email_invalid}
                  </div>
                )}
              </>
            )}

            {error && (
              <div style={{ color: "#C4604A", fontSize: 12.5, marginTop: 8, textAlign: "center" }}>
                {error === "trial_taken"
                  ? t.reg_trial_taken
                  : error === "password_short"
                  ? t.auth_error_password_short
                  : error === "generic"
                  ? t.reg_trial_error
                  : t.reg_trial_error}
              </div>
            )}

            <div style={{ marginTop: 12 }}>
              {/*
              <PrimaryButton onClick={handleSubmit} disabled={!canSubmit}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {submitting ? <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} /> : null}
                  {t.reg_trial_verify} {!submitting && <ArrowRight size={16} />}
                </span>
              </PrimaryButton>
              */}
            </div>

            {/*
            <button
              onClick={onBack}
              style={{
                marginTop: 10,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "15px 18px",
                borderRadius: 14,
                border: `1px solid ${C.hairline}`,
                background: "transparent",
                color: C.primary,
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 600,
                fontSize: 14.5,
                cursor: "pointer",
              }}
            >
              <ArrowLeft size={16} />
              {t.reg_trial_back}
            </button>
            */}
          </div>
        </div>
      </div>
    </div>
  );
}

export function RegistrationScreen({ t, authState, error, onSubmit, onSubmitTrial, wide }) {
  const [accountId, setAccountId] = useState("");
  const [view, setView] = useState("form"); // "form" | "switch" | "trial"
  const submitting = authState === "linking";

  const handleSubmit = () => {
    const trimmed = accountId.trim();
    if (!trimmed || submitting) return;
    onSubmit(trimmed);
  };

  if (view === "switch") {
    return <SwitchAccountScreen t={t} wide={wide} onBack={() => setView("form")} />;
  }

  if (view === "trial") {
    return (
      <TrialPeriodScreen
        t={t}
        authState={authState}
        error={error}
        onSubmit={onSubmitTrial}
        onBack={() => setView("form")}
        wide={wide}
        isBrowser={false}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: wide ? 40 : 20,
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div
          style={{
            borderRadius: 22,
            background: `linear-gradient(160deg, ${C.panel1} 0%, ${C.panel2} 140%)`,
            border: `1px solid ${C.hairline}`,
            padding: 22,
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: C.deep,
              border: `1px solid ${C.green}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <ShieldCheck size={24} color={C.greenBright} />
          </div>

          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 21,
              color: C.primary,
              letterSpacing: "-0.01em",
            }}
          >
            {t.reg_title}
          </div>
          <p style={{ color: C.secondary, fontSize: 13.5, lineHeight: 1.55, marginTop: 8 }}>
            {t.reg_desc}
          </p>

          <RegLinkButton t={t} label={t.reg_register} />

          {/*
          <button
            onClick={() => setView("trial")}
            style={{
              marginTop: 10,
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "15px 18px",
              borderRadius: 14,
              border: "none",
              background: `linear-gradient(120deg, ${C.violet} 0%, ${C.violetBright} 100%)`,
              color: "#0b0d13",
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: `0 8px 26px -8px ${C.violetBright}99`,
            }}
          >
            <Sparkles size={17} />
            {t.reg_trial_link}
          </button>
          */}

          <button
            onClick={() => setView("switch")}
            style={{
              marginTop: 10,
              width: "100%",
              padding: "15px 18px",
              borderRadius: 14,
              border: `1px solid ${C.hairline}`,
              background: "transparent",
              color: C.primary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 14.5,
              cursor: "pointer",
            }}
          >
            {t.reg_have_account}
          </button>
        </div>

        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              color: C.primary,
            }}
          >
            {t.reg_id_label}
          </div>
          <div style={{ fontSize: 12.5, color: C.mutedB, marginTop: 4, marginBottom: 12 }}>
            {t.reg_id_hint}
          </div>

          <input
            id="pj-account-id"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder={t.reg_id_placeholder}
            inputMode="numeric"
            style={{
              width: "100%",
              background: C.panel1,
              border: `1px solid ${error ? "#C4604A" : C.green}`,
              borderRadius: 14,
              color: C.primary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 15,
              textAlign: "center",
              padding: "16px 14px",
              outline: "none",
            }}
          />

          {error && (
            <div style={{ color: "#C4604A", fontSize: 12.5, marginTop: 8, textAlign: "center" }}>
              {t.reg_error}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <PrimaryButton onClick={handleSubmit} disabled={!accountId.trim() || submitting}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {submitting ? <Loader2 size={16} style={{ animation: "pjSpin 1s linear infinite" }} /> : null}
                {t.reg_verify} {!submitting && <ArrowRight size={16} />}
              </span>
            </PrimaryButton>
          </div>

          <SupportButton label={t.reg_support} />
        </div>
      </div>
    </div>
  );
}